import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse, ProposeTransaction } from '../../shared/services/types/api-schema.types';

@Injectable({
  providedIn: 'root',
})
export class DistributionService {
  private listSidebarYear = new BehaviorSubject<any[]>([]);
  listSidebarYear$ = this.listSidebarYear.asObservable();
  private distributionTypeSource = new BehaviorSubject<any | null>(null);
  distributionType$ = this.distributionTypeSource.asObservable();

  updateYearList(yearList: any[]): void {
    this.listSidebarYear.next(yearList);
  }

  constructor(private http: HttpClient) { }

  /**
   * Sets distribution type
   * @param distributionType
   */
  setDistributionType(distributionType: string) {
    this.distributionTypeSource.next(distributionType);
  }

  /**
   * Gets distribution all the users data
   * @returns
   */
  getDistributionUserData(
    mode:string,
    page?: number,
    limit?: number,
    search?: string
  ): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL
      }/admin/list-user?page=${page}&limit=${limit}&search=${search ? search : ''
      }&mode=${mode == 'all' ? '' : mode}`
    );
  }

  /**
   * Gets user distribution details in individual user
   * @param userId
   * @returns
   */
  getUserDistributionDetails(userId: any,mode:string): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL}/admin/user-distribution-report?user_id=${userId}&mode=${mode}`
    );
  }

  /**
   * Gets distribution monthly report
   * @param date
   * @returns
   */
  getDistributionMonthlyReport(
    month: any,
    year: any,
    category: string,
    page?: number,
    limit?: number
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/monthly-report?month=${month}&year=${year}&category=${category}&page=${page ?? ''
      }&limit=${limit ?? ''}`
    );
  }

  /**
   * Gets distribution years minimum-to-maximum
   * @returns
   */
  getDistributionYears(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/minimum-maximum-year`
    );
  }

  /**
   * Gets all the categories
   * @returns  Observable<any>
   */
  getDistributionCategories(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/list-category`
    );
  }

  /**
   * Gets distribution years and months by category id
   * @param id The id of the category to fetch the years and months for
   * @returns Observable<any>
   */
  getDistributionYearsAndMonthsByCategoryId(
    id: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/get-category/${id}`
    );
  }

  /**
   * Gets the current distribution mode
   * @returns Observable<ApiResponse<any>>
   */
  getDistributionMode(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/get-distribution-type`
    );
  }

  /**
   * Updates the distribution mode.
   * @param modeCode The code of the mode to switch to. 0 for manual and 1 for automated.
   * @param distributionId The id of the distribution to switch the mode for.
   * @returns Observable<ApiResponse<any>>
   */
  updateDistributionMode(
    modeCode: number,
    distributionId: string
  ): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/update-distribution-type?id=${distributionId}`,
      { distribution_type: modeCode }
    );
  }

  /**
   * Deletes a distribution user by their ID.
   *
   * @param userId The ID of the user to be deleted.
   * @returns Observable<ApiResponse<any>> indicating the result of the delete operation.
   */
  deletedDistributionUserById(userId: string, mode: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/delete-user/${userId}?mode=${mode}`
    );
  }

  /**
   * Gets the list of years for token distribution.
   * @returns Observable<ApiResponse<any>> containing the list of years.
   */
  getTokenDistributionYears(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/distribution-years`
    );
  }

  /**
   * Retrieves the distribution monthly report for a given month and year.
   * @param month The month for which to retrieve the report.
   * @param year The year for which to retrieve the report.
   * @returns Observable<ApiResponse<any>> containing the report for the given month and year.
   */
  getTokenDistributionMonthlyReport(
    month: any,
    year: any,
    page?: number,
    limit?: number,
    search?:string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/month-wise-report?month=${month}&year=${year}&page=${page ?? ''
      }&limit=${limit ?? ''}&search=${search ?? ''}`
    );
  }

  /**
   * Passes the distribution date for manual distribution.
   *
   * @param data The data containing the distribution date.
   * @returns Observable<ApiResponse<any>> indicating the result of the operation.
   */
  passDistributionDate(data: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/manual-distribution`,
      data
    );
  }

  /**
   * Retrieves the distribution monthly report for a given month and year,
   * including the distribution history.
   *
   * @param month The month for which to retrieve the report.
   * @param year The year for which to retrieve the report.
   * @param page The page number for pagination.
   * @param limit The number of items to retrieve per page.
   * @param status The status of the distribution to filter the report by.
   * @returns Observable<ApiResponse<any>> containing the report for the given month and year.
   */
  getTokenDistributionMonthlyHistoryReport(
    month: any,
    year: any,
    page?: number,
    limit?: number,
    status?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/month-wise-report?month=${month}&year=${year}&page=${page ?? ''
      }&limit=${limit ?? ''}&status=${status ?? ''}`
    );
  }

  /**
   * Gets distribution analytics
   * @returns distribution analytics
   */
  getDistributionAnalytics(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/analytics`
    );
  }

  /**
   * Checks if all distribution statuses are completed for a given month and year.
   *
   * @param month The month for which to check the status.
   * @param year The year for which to check the status.
   * @returns Observable<ApiResponse<any>> indicating the result of the check.
   */
  checkAllstatusTrue(
    month: any,
    year: any,
    page?: number
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/check-distribution-complete-status?month=${month}&year=${year}&page=${page ?? ''
      }`
    );
  }

  /**
   * Confirms the distribution process by sending a patch request with the provided data.
   *
   * @param data - The data to be sent for distribution confirmation.
   * @returns Observable<ApiResponse<any>> indicating the result of the confirmation.
   */
  distributionConfrimation(data: any): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/distribution-confirmation`,
      data
    );
  }

  /**
   * Retrieves the list of failed imports.
   *
   * @returns Observable<ApiResponse<any>> containing the list of failed imports.
   */
  getFailedImportsData(
    page?: number,
    limit?: number,
    search?: string,
    mode?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/list-failed-data?page=${page ?? ''
      }&limit=${limit ?? ''}&search=${search ?? ''}&mode=${mode ?? ''}`
    );
  }

  /**
   * Deletes a failed import by its ID.
   * @param id The ID of the failed import to be deleted.
   * @returns Observable<ApiResponse<any>> indicating the result of the deletion.
   */
  deletedFailedImportUserById(id: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/delete-failed-data?id=${id}`
    );
  }

  /**
   * Retrieves the global distribution history.
   *
   * @param page The page number to retrieve.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponse<any>> containing the global distribution history.
   */
  getGlobalDistributionHistory(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/distribution-history?page=${page ?? ''
      }&limit=${limit ?? ''}&search=${search ?? ''}`
    );
  }

  /**
   * Deletes all users with the given IDs.
   * @param data An array of IDs of the users to be deleted.
   * @returns Observable<ApiResponse<null>> indicating the result of the deletion.
   */
  deleteAllUsers(data: any,mode:string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      `${environment.API_BASE_URL}/admin/delete-users?mode=${mode}`,
      { body: { ids: data } }
    );
  }

  /**
   * Creates a new announcement or updates an existing one.
   *
   * @param data The data to be used for creating or updating the announcement.
   * @returns Observable<ApiResponse<any>> containing the result of the save operation.
   */
  saveAnnouncement(data: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/create-announcement`,
      data
    );
  }

  /**
   * Retrieves the list of all announcements.
   *
   * @param page The page number to retrieve.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponse<any>> containing the list of all announcements.
   */
  getAllAnnouncement(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/announcements?page=${page ?? ''
      }&limit=${limit ?? ''}&search=${search ?? ''}`
    );
  }

  /**
   * Retrieves an announcement by its ID.
   *
   * @param id The unique identifier of the announcement to retrieve.
   * @returns Observable<ApiResponse<any>> containing the announcement data.
   */
  getAnnouncementById(id: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/get-announcement/${id}`
    );
  }

  /**
   * Updates an existing announcement.
   *
   * @param id The unique identifier of the announcement to update.
   * @param data The data to be used for updating the announcement.
   * @returns Observable<ApiResponse<any>> containing the result of the update operation.
   */
  updateAnnouncement(id: string, data: any): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/update-announcement/${id}`,
      data
    );
  }

  /**
   * Deletes an announcement by its ID.
   *
   * @param id The unique identifier of the announcement to delete.
   * @returns Observable<ApiResponse<any>> indicating the result of the deletion.
   */
  deleteAnnouncementById(id: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/delete-announcement/${id}`
    );
  }

  /**
   * Retrieves all users without pagination.
   *
   * @returns Observable<ApiResponse<any>> containing all user data.
   */
  getAllUsersWithoutPagination(mode:string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/list-all-users?mode=${mode}`
    );
  }

  getVisitorManagementList(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/terms-acceptance-count`
    );
  }

  /**
   * Retrieves the details of visitors who have accepted terms.
   *
   * @param page The page number to retrieve for pagination.
   * @param limit The number of items per page to retrieve.
   * @param search The search query to filter the visitor details.
   * @returns Observable<any> containing the details of accepted visitors.
   */
  getVistorsDetails(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL
      }/admin/accepted-users?page=${page}&limit=${limit}&search=${search ? search : ''
      }`
    );
  }

  /**
   * Retrieves the list of banners.
   *
   * @param page The page number to retrieve for pagination.
   * @param limit The number of items per page to retrieve.
   * @param search The search query to filter the banners by.
   * @returns Observable<ApiResponse<any>> containing the list of banners.
   */
  getBanners(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/list-banner-images?page=${page ?? ''
      }&limit=${limit ?? ''}&search=${search ?? ''}`
    );
  }

  /**
   * Deletes a banner image.
   *
   * @param id The unique identifier of the banner image to delete.
   * @returns Observable<ApiResponse<any>> indicating the result of the deletion.
   */
  deleteBanner(id: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/delete-banner-image/${id}`
    );
  }

  /**
   * Retrieves a banner image by its ID.
   *
   * @param id The unique identifier of the banner image to retrieve.
   * @returns Observable<ApiResponse<any>> containing the banner image data.
   */
  getBannerById(id: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/get-banner-image/${id}`
    );
  }

  /**
   * Creates a new banner.
   *
   * @param data The data to be used for creating the banner.
   * @returns Observable<ApiResponse<any>> containing the result of the create operation.
   */
  createBanner(data: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/create-banner-image`,
      data
    );
  }

  /**
   * Updates an existing banner.
   *
   * @param id The unique identifier of the banner to update.
   * @param data The data to be used for updating the banner.
   * @returns Observable<ApiResponse<any>> containing the result of the update operation.
   */
  updateBanner(id: string, data: any): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/update-banner-image/${id}`,
      data
    );
  }

  /**
   * Uploads a file to the server.
   *
   * @param fileToUpload The file to be uploaded.
   * @returns Observable<any> containing the result of the upload.
   */
  fileUpload(fileToUpload: any): Observable<any> {
    const endpoint = `${environment.FILE_UPLOAD_URL}`;
    const formData: FormData = new FormData();
    formData.append('media', fileToUpload, fileToUpload.name);
    formData.append('service', 'test');
    return this.http.post(endpoint, formData);
  }


  /**
   * Retrieves the list of voice announcements.
   *
   * @param page The page number to retrieve.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponse<any>> containing the list of voice announcements.
   */
  listAudioAnnouncement(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/list-voice-announcements?page=${page ?? ""}&limit=${limit ?? ""
      }&search=${search ?? ""}`
    );
  }



  /**
   * Deletes a voice announcement by its ID.
   *
   * @param id The unique identifier of the voice announcement to delete.
   * @returns Observable<ApiResponse<any>> indicating the result of the deletion.
   */
  deleteAudioAnnouncementById(id: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/delete-voice-announcement/${id}`
    );
  }

  /**
 * Retrieves a voice announcement by its ID.
 *
 * @param id The unique identifier of the voice announcement to retrieve.
 * @returns Observable<ApiResponse<any>> containing the voice announcement data.
 */
  getAudioAnnouncementById(id: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/get-voice-announcement/${id}`
    );
  }


  /**
   * Updates an existing audio announcement.
   *
   * @param id The unique identifier of the audio announcement to update.
   * @param data The data to be used for updating the audio announcement.
   * @returns Observable<ApiResponse<any>> containing the result of the update operation.
   */
  updateAudioAnnouncement(id: string, data: any): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/update-voice-announcement/${id}`,
      data
    );
  }


  /**
   * Creates a new audio announcement.
   *
   * @param data The data to be used for creating the audio announcement.
   * @returns Observable<ApiResponse<any>> containing the result of the create operation.
   */
  createAudioAnnouncement(data: any): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/create-voice-announcement`,
      data
    );
  }


  /**
   * Deletes all failed imports.
   *
   * @param data An array of IDs of the failed imports to be deleted.
   * @returns Observable<ApiResponse<null>> indicating the result of the deletion.
   */
  deleteAllFailedImports(data: any,mode:string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      `${environment.API_BASE_URL}/admin/delete-all-failed-imports?mode=${mode}`,
      { body: { ids: data } }
    );
  }

  /**
   * Retrieves the list of deleted failed imports.
   *
   * @param page The page number to retrieve for pagination.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponse<any>> containing the list of deleted failed imports.
   */
  getDeletedFailedImportsData(
    mode: string,
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/deleted-failed-imports?page=${page ?? ''}&limit=${limit ?? ''}&search=${search ?? ''}&mode=${mode ?? ''}`
    );
  }


  /**
   * Retrieves all deleted failed imports without pagination.
   *
   * @returns Observable<ApiResponse<any>> containing all deleted failed imports.
   */
  getAllDeletedFailedImportsWithoutPagination(mode:string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/list-deleted-imports?mode=${mode ?? ''}`
    );
  }

  /**
   * Retrieves all failed imports without pagination.
   *
   * @returns Observable<ApiResponse<any>> containing all failed imports.
   */
  getAllFailedImportsWithoutPagination(mode?: string,page?: number, ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/list-failed-imports?limit=100000&page=${page ?? ''}&mode=${mode ?? ''}` // Keep limit=100000 to avoid large data loads that can slow or fail the API.
    );
  }

  /**
   * Retrieves a failed import by its ID.
   */
  getFailedImportReasonForId(id: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/get-failed-import/${id}`
    );
  }

  /**
   * Retrieves a deleted failed import by its ID.
   *
   * @param id The unique identifier of the deleted failed import to retrieve.
   * @returns Observable<ApiResponse<any>> containing the deleted failed import data.
   */
  getDeletedFailedImportReasonForId(id: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/get-deleted-import/${id}`
    );
  }


  /**
   * Fetches the list of users for USDT transfer.
   *
   * @param page The page number to retrieve for pagination.
   * @param limit The number of items per page to retrieve.
   * @param search The search query to filter the users.
   * @returns Observable<any> containing the list of USDT transfer users.
   */
  getUsdtTransferUserData(
    page?: number,
    limit?: number,
    search?: string,
    status?: string,
    token?: string

  ): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL
      }/admin/usdt-users?page=${page}&limit=${limit}&search=${search ? search : ''
      }&status=${status}&token=${token ?? ''}`
    );
  }


  /**
   * Deletes a USDT distribution user by their ID.
   *
   * @param userId The ID of the USDT user to be deleted.
   * @returns Observable<ApiResponse<any>> indicating the result of the delete operation.
   */
  deleteUsdtDistributionUserById(userId: string, token: string, status: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/delete-usdt-user/${userId}?token=${token}&status=${status}`
    );
  }

  /**
   * Deletes all USDT distribution users with the given IDs.
   *
   * @param data An array of IDs of the USDT users to be deleted.
   * @returns Observable<ApiResponse<null>> indicating the result of the deletion.
   */
  deleteAllUsdtUsers(data: any, token: string, status: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      `${environment.API_BASE_URL}/admin/delete-usdt-users?token=${token}&status=${status}`,
      { body: { ids: data } }
    );
  }


  /**
   * Retrieves all USDT users without pagination.
   *
   * @returns Observable<ApiResponse<any>> containing all USDT user data.
   */
  getAllUsdtUsersWithoutPagination(token: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/list-usdt-users?token=${token}`
    );
  }

  /**
   * Retrieves the list of failed USDT imports.
   *
   * @param page The page number to retrieve for pagination.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponse<any>> containing the list of failed USDT imports.
   */
  getUsdtFailedImportsData(
    page?: number,
    limit?: number,
    search?: string,
    token?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/usdt-failed-imports?page=${page ?? ''
      }&limit=${limit ?? ''}&search=${search ?? ''}&token=${token}`
    );
  }


  /**
   * Retrieves all failed USDT imports without pagination.
   *
   * @returns Observable<ApiResponse<any>> containing all failed USDT imports.
   */
  getAllUsdtFailedImportsWithoutPagination(token: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/list-usdt-failed-imports?token=${token}`
    );
  }

  /**
  * Retrieves a failed USDT import by its ID.
  *
  * @param id The ID of the failed USDT import to retrieve.
  * @returns Observable<ApiResponse<any>> containing the failed USDT import data.
  */
  getUsdtFailedImportReasonForId(id: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/get-usdt-failed-import/${id}`
    );
  }

  /**
   * Deletes a failed USDT import by its ID.
   *
   * @param id The ID of the failed USDT import to delete.
   * @returns Observable<ApiResponse<any>> indicating the result of the deletion.
   */
  deletedUsdtFailedImportUserById(id: string, token: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/delete-usdt-failed-import/${id}?token=${token}`
    );
  }

  /**
   * Deletes all failed USDT imports with the given IDs.
   *
   * @param data An array of IDs of the failed USDT imports to be deleted.
   * @returns Observable<ApiResponse<null>> indicating the result of the deletion.
   */
  deleteAllUsdtFailedImports(data: any, token: string): Observable<ApiResponse<null>> {
    return this.http.delete<ApiResponse<null>>(
      `${environment.API_BASE_URL}/admin/delete-usdt-failed-imports?token=${token}`,
      { body: { ids: data } }
    );
  }

  /**
   * Retrieves the list of deleted USDT failed imports.
   *
   * @param page The page number to retrieve for pagination.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponse<any>> containing the list of deleted USDT failed imports.
   */
  getDeletedUsdtFailedImportsData(
    page?: number,
    limit?: number,
    search?: string,
    token?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/deleted-usdt-failed-imports?page=${page ?? ''}&limit=${limit ?? ''}&search=${search ?? ''}&token=${token}`
    );
  }

  /**
   * Retrieves all deleted USDT failed imports without pagination.
   *
   * @returns Observable<ApiResponse<any>> containing all deleted USDT failed imports.
   */
  getAllDeletedUsdtFailedImportsWithoutPagination(token: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/list-deleted-usdt-imports?token=${token}`
    );
  }

  /**
   * Retrieves a deleted USDT failed import by its ID.
   *
   * @param id The ID of the deleted USDT failed import to retrieve.
   * @returns Observable<ApiResponse<any>> containing the deleted USDT failed import data.
   */
  getDeletedUsdtFailedImportReasonForId(id: string): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/get-deleted-usdt-import/${id}`
    );
  }


  /**
   * Retrieves the list of USDT transaction history.
   *
   * @param page The page number to retrieve for pagination.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the results by.
   * @returns Observable<ApiResponse<any>> containing the list of USDT transaction history.
   */
  getUsdtTransactionHistoryData(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/usdt-users?page=${page ?? ''}&limit=${limit ?? ''}&search=${search ?? ''}&status=2`
    );
  }



  /**
   * Retrieves the failed token distribution monthly report for the specified month and year.
   *
   * @param month The month for which to retrieve the report.
   * @param year The year for which to retrieve the report.
   * @param page The page number to retrieve for pagination. Defaults to 1.
   * @param limit The number of items to retrieve per page. Defaults to 10.
   * @param searchQuery The search query to apply to the report. Defaults to an empty string.
   * @returns Observable<ApiResponse<any>> containing the failed token distribution monthly report.
   */
  getFailedTokenDistributionMonthlyReport(
    month: any,
    year: any,
    page?: number,
    limit?: number,
    searchQuery?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/month-wise-failed-distributions?month=${month}&year=${year}&page=${page ?? ''
      }&limit=${limit ?? ''}&search=${searchQuery ?? ''}`
    );
  }

  /**
   * Checks if the distribution data is being processed.
   *
   * @returns Observable<any> containing the result of the check.
   */
  canCallDistributionData(): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL}/admin/user-status`
    );
  }


  /**
   * Transfers the specified amount of TBNB to the admin wallet.
   *
   * @param count The amount of TBNB to transfer.
   * @returns Observable<any> containing the result of the transfer.
   */
  tbnbTransfer(count: number): Observable<any> {
    return this.http.post(
      `${environment.API_BASE_URL}/admin/transfer-tbnb`, {
      count
    }
    );
  }

  /**
   * Retrieves the balance of the given wallet address on the Binance Smart Chain (BSC).
   *
   * @param walletAddress The wallet address for which to retrieve the balance.
   * @returns Observable<any> containing the balance of the given wallet address.
   */
  getTbnbBalance(walletAddress: string): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL}/admin/tbnb-balance?address=${walletAddress}`,
    );
  }


  /**
   * Retrieves the distribution details counts for the specified month and year.
   *
   * @param month The month for which to retrieve the distribution counts.
   * @param year The year for which to retrieve the distribution counts.
   * @returns Observable<ApiResponse<any>> containing the distribution counts.
   */
  distributionDetailsCounts(month: any, year: any): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/distribution-count?month=${month}&year=${year}`
    );
  }


  /**
   * Retrieves the total AOC value for the specified month and year.
   *
   * @param month The month for which to retrieve the total AOC value.
   * @param year The year for which to retrieve the total AOC value.
   * @returns Observable<ApiResponse<any>> containing the total AOC value.
   */
  getTotalAocValueForApprove(month: any, year: any): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/aoc-monthly-total?month=${month}&year=${year}`
    );
  }


  /**
   * Retrieves the complete token distribution history for the specified month and year.
   *
   * @param month The month for which to retrieve the complete token distribution history.
   * @param year The year for which to retrieve the complete token distribution history.
   * @param page The page number to retrieve for pagination. Defaults to 1.
   * @param limit The number of items to retrieve per page. Defaults to 10.
   * @param status The status of the distribution. Defaults to an empty string.
   * @returns Observable<ApiResponse<any>> containing the complete token distribution history.
   */
  getTokenDistributionCompleteHistory(
    month: any,
    year: any,
    page?: number,
    limit?: number,
    status?: string,
    search?: string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/distribution-history?month=${month}&year=${year}&page=${page ?? ''
      }&limit=${limit ?? ''}&status=${status ?? ''}&search=${search ?? ''}`
    );
  }


/**
 * Retrieves the list of testers.
 *
 * @param page The page number to retrieve for pagination.
 * @param limit The number of items to retrieve per page.
 * @param search The search query to filter the testers.
 * @returns Observable<any> containing the list of testers.
 */
  getTestersData(
    page?: number,
    limit?: number,
    search?: string,
    order?: string
  ): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL
      }/admin/list-subscribers?page=${page}&limit=${limit}&search=${search ? search : ''}&order=${order ? order : ''}`
    );
  }


  /**
   * Retrieves all testers without pagination.
   *
   * @returns Observable<any> containing all tester data.
   */
  getAllTestersWithoutPagination(): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL
      }/admin/list-subscribers`
    );
  }


  /**
   * Retrieves the list of newsletter subscribers.
   *
   * @param page The page number to retrieve for pagination.
   * @param limit The number of items to retrieve per page.
   * @param search The search query to filter the newsletter subscribers.
   * @returns Observable<any> containing the list of newsletter subscribers.
   */
  getNewsLetterUsersData(
    page?: number,
    limit?: number,
    search?: string
  ): Observable<any> {
    return this.http.get(
      `${environment.API_BASE_URL}/admin//list-newsletter-subscribers?page=${page}&limit=${limit}&search=${search ? search : ''}`
    );
  }



  /**
   * Deletes all the given tester ids.
   * @param data The list of tester ids to delete.
   * @returns Observable<ApiResponse<null>> containing the response.
   */
  deleteAlltesters(data: any): Observable<ApiResponse<null>> {
    return this.http.patch<ApiResponse<null>>(
      `${environment.API_BASE_URL}/admin/delete-subscribers`,
      {ids: data} 
    );
  }

  /**
   * Gets proposed transaction
   * @param year
   * @param month
   * @returns
   */
  getProposedTransaction(year: any, month: any) {
    return this.http.get(`${environment.API_BASE_URL}/admin/transaction-proposals/${year}/${month}`)
  }

  /**
   * Posts proposed transaction
   * @param payload
   * @returns proposed transaction
   */
  postProposedTransaction(payload: ProposeTransaction): Observable<ApiResponse<ProposeTransaction>> {
    return this.http.post<ApiResponse<ProposeTransaction>>(`${environment.API_BASE_URL}/admin/create-transaction-proposal`, payload);
  }


  /**
   * Retrieves the total number of holders of the distribution analytics.
   *
   * @returns Observable<ApiResponse<any>> containing the total number of holders.
   */
  getDistributionAnalyticsHolders(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/total-holders`
    );
  }

  /**
   * Retrieves the user statistics for all users.
   *
   * @returns Observable<ApiResponse<any>> containing the user statistics.
   */
  getAllUsersStates(): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL}/admin/user-stats`
    );
  }


  /**
   * Retrieves the excess distribution data.
   * 
   * @param page The page number to retrieve for pagination. Defaults to 1.
   * @param limit The number of items to retrieve per page. Defaults to 10.
   * @param search The search query to filter the results by. Defaults to an empty string.
   * @returns Observable<ApiResponse<any>> containing the excess distribution data.
   */
  getExcessDistributionData(   
    page?: number,
    limit?: number,
    search?:string
  ): Observable<ApiResponse<any>> {
    return this.http.get<ApiResponse<any>>(
      `${environment.API_BASE_URL
      }/admin/excess-distribution-report?page=${page ?? ''
      }&limit=${limit ?? ''}&search=${search ?? ''}`
    );
  }
}


